/**
 * The public home page.
 *
 * The impact panel is the honest version of `01_architecture.md` §12: real
 * aggregates only, and an explicit unavailable state until donations exist —
 * never a zero that would read as "we have always funded nothing".
 */

import { apiFetch, useAuthState } from '@qalam/shared/frontend';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export interface PublicImpact {
  available: boolean;
  seatsFunded: number | null;
  studentsReached: number | null;
  activeCampaigns: unknown[];
}

export default function HomePage() {
  const { t } = useTranslation();
  const { isAuthenticated, me, signOut } = useAuthState();
  const [impact, setImpact] = useState<PublicImpact | null>(null);
  const [impactError, setImpactError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PublicImpact>('/api/public/impact')
      .then((payload) => {
        if (!cancelled) setImpact(payload);
      })
      .catch(() => {
        if (!cancelled) setImpactError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main data-testid="home">
      <header>
        <h1>{t('app.name')}</h1>
        <nav>
          <Link to="/give">{t('nav.campaigns')}</Link>
          {isAuthenticated ? (
            <button type="button" onClick={() => void signOut()}>
              {t('app.signOut')}
            </button>
          ) : (
            <a href="/api/auth/oauth/google">{t('app.signIn')}</a>
          )}
        </nav>
      </header>

      <p>{t('app.tagline')}</p>

      <section aria-labelledby="impact-heading">
        <h2 id="impact-heading">{t('impact.title')}</h2>
        {impactError ? (
          <p>{t('common.error')}</p>
        ) : impact === null ? (
          <p>{t('app.loading')}</p>
        ) : impact.available ? (
          <dl>
            <dt>{t('impact.seatsFunded')}</dt>
            <dd>{impact.seatsFunded}</dd>
            <dt>{t('impact.studentsReached')}</dt>
            <dd>{impact.studentsReached}</dd>
          </dl>
        ) : (
          <p data-testid="impact-empty">{t('impact.unavailable')}</p>
        )}
      </section>

      {me ? (
        <p>
          {t('app.signedInAs')}: {me.email ?? me.donorId}
        </p>
      ) : null}
    </main>
  );
}
